import stackToArray from './stackToArray';

/**
 * Serialize context to JSON.
 */
function serializeContext(ctx: any) {
  if (ctx == null) return ctx;

  switch (typeof ctx) {
    case 'string':
      return ctx;
    case 'object':
      try {
        return JSON.stringify(ctx);
      } catch (e) {
        return ctx.toString();
      }
    default:
      return ctx.toString();
  }
}

export type EventLogConfig = {
  logUrl: string;
  appName?: string;
  appVersion?: string;
  extenders?: [
    {
      extend: (log: EventLogEventInput) => EventLogEventInput;
    }
  ];
};

type EventLogApp = {
  name: string;
  version: string;
};

type EventLogLocation = Pick<Location, 'href' | 'pathname' | 'host' | 'search'>;

export type EventLogEvent = {
  app: EventLogApp;
  user: {
    id?: string;
    device?: string;
    agent: string;
    region?: string;
    viewerId?: string;
    platform?: string;
    appVersion?: string;
  };
  location: EventLogLocation;
  error: any;
  info: any;
  warn: any;
  context: string;
  data: any;
};

export type EventLogEventInput = Pick<EventLogEvent, 'location'> &
  Partial<
    Pick<EventLogEvent, 'error' | 'info' | 'warn' | 'data'> & {
      context?: any;
      user?: {
        userId?: string;
        device?: {
          deviceUid: string;
        };
        region?: string;
        platform?: string;
        appVersion?: string;
      };
    }
  >;

/**
 * Howto use:
 * - import EventLog from '@tv2/lib-core/logging/eventLogger';
 * - const options = { logUrl: '', appName: 'Test', ''}
 * - const Log = new EventLog(options);
 * - Log.event(new Error('test'));
 */
export default class EventLog {
  logUrl: string;
  app: EventLogApp;
  events: EventLogEvent[];

  /**
   * Initiate eventLogger with
   * new EventLog(options);
   * @param {Object} config Object consisting of {logUrl, appName and appVersion}
   */
  constructor(config: EventLogConfig) {
    const { logUrl, appName, appVersion } = config;
    if (!logUrl) throw new Error('logUrl is not set');
    if (!appName) throw new Error('appName is not set');
    if (!appVersion) throw new Error('appVersion is not set');

    this.logUrl = logUrl;
    this.app = {
      name: appName,
      version: appVersion,
    };
    this.events = [];
    let backoff = 1000;
    const handler = () => {
      if (this.events.length > 0) {
        const eventsToSend = this.events;
        this.events = [];
        const _ = fetch(this.logUrl, {
          ...EventLog.requestHeader,
          body: JSON.stringify(eventsToSend),
        })
          .then(res => {
            backoff = 1000;
            return true;
          })
          .catch(() => {
            this.events.push(...eventsToSend);
            backoff = Math.min(backoff * 2, 60_000);
            setTimeout(handler, backoff);
          });
      }
    };
    setTimeout(handler, backoff);
  }

  /**
   * Default requestHeader for fetch
   * @return {Object}
   */
  static get requestHeader() {
    return {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
  }

  addEvent(eventBody: EventLogEvent) {
    this.events.push(eventBody);
  }

  /**
   * Private method for logging error and info events.
   * @return {EventLog}
   */
  #event = ({ user, location, error, info, warn, context, data }: EventLogEventInput) => {
    const { href, pathname, host, search } = location;
    this.addEvent({
      app: this.app,
      user: {
        id: user?.userId?.toString(),
        device: user?.device?.deviceUid,
        agent: navigator.userAgent,
        region: user?.region,
        platform: user?.platform,
        appVersion: user?.appVersion,
      },
      location: {
        href,
        pathname,
        host,
        search,
      },
      error,
      info,
      warn,
      context: serializeContext(context),
      data,
    });
    return this;
  };

  /**
   * Public function for emitted infoEvents
   * Takes user, location, info, context and changes it into an Object
   * emitting a "log" sequence
   * @return {EventLog}
   */
  info = (body: EventLogEventInput) => {
    const { type = 'Info', message = 'Unknown', raw } = body.info ?? {};
    const info = { name: type, message, raw: JSON.stringify(raw) };
    return this.#event({ ...body, info });
  };

  warn = (body: EventLogEventInput) => {
    const { type = 'Warn', message = 'Unknown', raw } = body.warn ?? {};
    const warn = { name: type, message, raw: JSON.stringify(raw) };
    return this.#event({ ...body, warn });
  };

  /**
   * Public function for emitted errorEvents
   * Takes user, location, error, context and changes it into an Object
   * emitting a "log" sequence
   * @return {EventLog}
   */
  error = (body: EventLogEventInput) => {
    const { type = 'Error', message = 'Unknown', stack, raw, name } = body.error ?? {};
    let rawAsJson = '{}';
    try {
      rawAsJson = JSON.stringify(raw);
      // eslint-disable-next-line no-empty
    } catch (_) {}
    const error = {
      name: name ?? type,
      message,
      raw: rawAsJson,
      stack: Array.isArray(stack) ? stack : stackToArray(stack),
    };

    return this.#event({ ...body, error });
  };

  /**
   * @deprecated Use `info` or `error` instead.
   *
   * Public function for emitted errorEvents
   * Takes user, location, error, context and changes it into an Object
   * emitting a "log" sequence
   * @return {EventLog}
   */
  event = this.error;
}
